import React, { useState, useEffect } from 'react';
import { Play, Film, Clock, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const HowToApply = () => {
  const { t } = useLanguage();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTutorial, setSelectedTutorial] = useState(null);

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const response = await axios.get(`${API}/api/tutorials`);
        setTutorials(response.data.tutorials || []);
        if (response.data.tutorials && response.data.tutorials.length > 0) {
          setSelectedTutorial(response.data.tutorials[0]);
        }
      } catch (error) {
        console.error('Failed to fetch tutorials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorials();
  }, []);

  return (
    <>
      {/* Hero Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Film className="h-10 w-10" />
            <h1 className="text-4xl md:text-5xl font-bold">{t('howToApply')}</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {t('watchTutorials')}
          </p>
        </div>
      </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-12">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            </div>
          ) : tutorials.length === 0 ? (
            <div className="text-center py-16">
              <Film className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-600 mb-2">{t('comingSoon')}</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                {t('workingOnTutorials')}
              </p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Video Player Section */}
              <div className="lg:col-span-2">
                {selectedTutorial && (
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="aspect-video bg-black">
                      <iframe
                        src={selectedTutorial.embed_url}
                        title={selectedTutorial.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid="tutorial-video-player"
                      />
                    </div>
                    <div className="p-6">
                      <h2 className="text-2xl font-bold mb-2" data-testid="tutorial-title">
                        {selectedTutorial.title}
                      </h2>
                      {selectedTutorial.description && (
                        <p className="text-gray-600" data-testid="tutorial-description">
                          {selectedTutorial.description}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Tutorial List Sidebar */}
              <div className="lg:col-span-1">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  {t('allTutorials')}
                </h3>
                <div className="space-y-3">
                  {tutorials.map((tutorial, index) => (
                    <button
                      key={tutorial._id}
                      onClick={() => setSelectedTutorial(tutorial)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 flex items-center gap-4 group ${
                        selectedTutorial?._id === tutorial._id
                          ? 'bg-black text-white shadow-lg'
                          : 'bg-white hover:bg-gray-100 shadow-sm hover:shadow-md'
                      }`}
                      data-testid={`tutorial-item-${index}`}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedTutorial?._id === tutorial._id ? 'bg-white/20' : 'bg-black/10'
                      }`}>
                        <Play className={`h-5 w-5 ${
                          selectedTutorial?._id === tutorial._id ? 'text-white' : 'text-black'
                        }`} />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-medium truncate">{tutorial.title}</h4>
                        {tutorial.description && (
                          <p className={`text-sm truncate ${
                            selectedTutorial?._id === tutorial._id ? 'text-gray-300' : 'text-gray-500'
                          }`}>
                            {tutorial.description}
                          </p>
                        )}
                      </div>
                      <ChevronRight className={`h-5 w-5 flex-shrink-0 transition-transform group-hover:translate-x-1 ${
                        selectedTutorial?._id === tutorial._id ? 'text-white' : 'text-gray-400'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tips Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center mb-8">{t('applicationTips')}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold">1</span>
                </div>
                <h3 className="font-semibold mb-2">{t('cleanSurface')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('cleanSurfaceTip')}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold">2</span>
                </div>
                <h3 className="font-semibold mb-2">{t('temperature')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('temperatureTip')}
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-black/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold">3</span>
                </div>
                <h3 className="font-semibold mb-2">{t('takeYourTime')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('takeYourTimeTip')}
                </p>
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default HowToApply;
